import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { message, Tabs, Modal, Input } from "antd";
import { useAuthContext } from "@app/authContext";
import {
  completeTask,
  DocInfo,
  fetchAndAssignOrderTask,
  fetchOrder,
  fetchOrderModel,
  fetchOrderTaskId,
  fetchOrderTaskRoutes,
  FileItem,
  Order,
  OrderModelResponse,
  TaskRoute,
} from "@services/api";
import { Comments } from "@app/comments/Comments";
import BankOffer from "./components/BankOffer";
import CustomerCard from "./components/CustomerCard";
import ProductBgCard from "./components/ProductBgCard";
import styles from "./bankOrderPage.module.css";
import BankTaskInfo from "./components/BankTaskInfo";
import SignDocsCard from "./components/SignDocsCard";
import TaskRouteButtons from "./components/TaskRouteButtons";
import DocCard from "./components/DocCard";
import { SafetyCertificateTwoTone } from "@ant-design/icons";
import FilesAttach from "./components/FilesAttach";
import DocBgCard from "./components/DocBgCard";
import DocDownloadCard from "./components/DocDownloadCard";

interface Props {
  orderId: string;
}

const footerStyle: CSSProperties = {
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 998,
  background: "#fff",
  padding: "0 16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderTop: "1px solid #f0f0f0",
  height: 68,
  boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
};

const contentStyle: CSSProperties = {
  marginBottom: 48,
};

const MODAL_TITLE = "Отправка заявки в банк";
const DEFAULT_ROUTE: TaskRoute = { key: "__DEFAULT__", value: "Отправить в банк" };

// утилита задержки
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

const DOC_OTHER = "otherOrderDocs" as const;
const DOC_BG_DRAFT = "bg_guaranteeDraft" as const;

function cloneDocs(docs: DocInfo[]) {
  const cloneDoc = (d: DocInfo): DocInfo => ({
    docType: d.docType,
    files: d.files.map(f => ({ ...f })),
  });
  return docs.map(cloneDoc);
}

function mergeDoc(docs: DocInfo[], docType: string, newFiles: FileItem[] ): void {
  const idx = docs.findIndex(d => d.docType === docType);
  if (idx === -1) {
    docs.push({ docType: docType, files: newFiles });
  } else {
    const oldFiles = docs[idx].files || [];
    docs[idx].files = [...oldFiles, ...newFiles];
  }
}

function replaceDoc(docs: DocInfo[], docType: string, newFiles: FileItem[] ): void {
  const idx = docs.findIndex(d => d.docType === docType);
  if (idx === -1) {
    docs.push({ docType: docType, files: newFiles });
  } else {
    docs[idx].files = newFiles;
  }
}

export default function BankOrderTab({ orderId }: Props) {
  const { auth } = useAuthContext();
  const accessToken = auth?.access_token ?? "";

  const [order, setOrder] = useState<Order | null>(null);
  const [model, setModel] = useState<OrderModelResponse | null>(null);
  const [routes, setRoutes] = useState<TaskRoute[]>([]);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  // modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<TaskRoute | null>(null);
  const [comment, setComment] = useState("");
  const [attachedFilesOther, setAttachedFilesOther] = useState<FileItem[]>([]);
  const [attachedFilesBgDraft, setAttachedFilesBgDraft] = useState<FileItem[]>([]);

  type LoadResult = { taskId: string | null; orderStatusCode?: string | null };

  /**
   * Загрузка данных.
   * @param opts.silent - если true, не показываем глобальный спиннер
   * @returns taskId и orderStatus.code
   */
  const loadData = useCallback(
    async (opts?: { silent?: boolean }): Promise<LoadResult> => {
      const silent = Boolean(opts?.silent);

      if (!orderId) {
        setOrder(null);
        setModel(null);
        setRoutes([]);
        setTaskId(null);
        return { taskId: null, orderStatusCode: null };
      }

      if (!silent) setLoading(true);

      let fetchedTaskId: string | null = null;
      let orderStatusCode: string | null | undefined = null;

      try {
        // 1) Базовые данные
        const [orderRes, modelRes] = await Promise.allSettled([
          fetchOrder(orderId, accessToken),
          fetchOrderModel(orderId, accessToken),
        ]);

        if (orderRes.status === "fulfilled") {
          setOrder(orderRes.value);
          orderStatusCode = orderRes.value?.orderStatus?.code ?? null;
        } else {
          message.error(
            orderRes.reason?.message ??
            String(orderRes.reason ?? "Ошибка получения данных по заявке")
          );
        }

        if (modelRes.status === "fulfilled") {
          setModel(modelRes.value);
        } else {
          message.error(
            modelRes.reason?.message ??
            String(modelRes.reason ?? "Ошибка получения расширенных данных по заявке")
          );
        }

        // 2) Наличие задачи
        fetchedTaskId = await fetchOrderTaskId(orderId, accessToken).catch(() => {
          message.error("Ошибка получения информации о задаче");
          return null;
        });
        setTaskId(fetchedTaskId || null);

        // 3) Маршруты
        if (fetchedTaskId) {
          try {
            // назначение делаем только в явной загрузке (не в тихих повторах)
            if (!silent) {
              await fetchAndAssignOrderTask(fetchedTaskId, accessToken);
            }
          } catch {
            if (!silent) message.error("Ошибка получения данных по задаче");
          }

          try {
            const rts = await fetchOrderTaskRoutes(fetchedTaskId, accessToken);
            setRoutes(rts.length > 0 ? rts : [DEFAULT_ROUTE]);
          } catch {
            message.error("Ошибка получения маршрутов задачи");
            setRoutes([]);
          }
        } else {
          setRoutes([]); // задачи нет — маршрутов нет
        }
      } finally {
        if (!silent) setLoading(false);
      }

      return { taskId: fetchedTaskId, orderStatusCode };
    },
    [orderId, accessToken]
  );

  // Доп. обновление: если status == bgReworkAgent и taskId отсутствует — ждём появления/смены
  const ensureReworkHasTaskOrStatusChanged = useCallback(
    async (initial: LoadResult) => {
      let attempts = 0;
      let current = initial;

      while (
        current.orderStatusCode === "bgReworkAgent" &&
        !current.taskId &&
        attempts < 10
      ) {
        attempts += 1;
        await delay(1000);
        current = await loadData({ silent: true });
      }
      return current;
    },
    [loadData]
  );

  // Доп. обновление после комплита: пока taskId не пропадёт/не сменится (ВСЁ silent)
  const ensureTaskIdChangedAfterComplete = useCallback(
    async (completedTaskId: string) => {
      let attempts = 0;
      let current = await loadData({ silent: true }); // сразу тихая перезагрузка

      while (current.taskId === completedTaskId && attempts < 10) {
        attempts += 1;
        await delay(1000);
        current = await loadData({ silent: true });
      }

      return current;
    },
    [loadData]
  );

  useEffect(() => {
    (async () => {
      const first = await loadData(); // первичная загрузка — можно не silent
      await ensureReworkHasTaskOrStatusChanged(first);
    })();
  }, [loadData, ensureReworkHasTaskOrStatusChanged]);

  if (loading) {
    return <div className="row muted">Загрузка...</div>;
  }

  if (!orderId) {
    return <div className="row muted">Ошибка</div>;
  }

  const handleSubmit = async (
    routeCode: string,
    commentText: string,
    newFilesOther: FileItem[],
    newFilesBgDraft: FileItem[] = []
  ) => {
    try {
      if (!taskId) {
        throw new Error("No taskId");
      }
      setSubmitting(true);

      const bankRoute = routeCode === DEFAULT_ROUTE.key ? "" : routeCode;

      const currentDocs = model?.components?.orderDocuments || [];
      const docs = cloneDocs(currentDocs);
      mergeDoc(docs, DOC_OTHER, newFilesOther);
      if (newFilesBgDraft.length > 0) {
        replaceDoc(docs, DOC_BG_DRAFT, newFilesBgDraft);
      }
      await completeTask(taskId, "sendToBank", commentText, bankRoute, docs, accessToken);

      // 1) ждём исчезновения/смены taskId — ТИХО
      const afterComplete = await ensureTaskIdChangedAfterComplete(taskId);

      // 2) если пришёл bgReworkAgent без taskId — допуллим этот кейс — ТИХО внутри
      await ensureReworkHasTaskOrStatusChanged(afterComplete);

      message.success("Заявка отправлена в банк");
    } catch (e) {
      message.error("Не удалось отправить заявку в банк");
      console.error("Failed to submit task", e);
    } finally {
      setSubmitting(false);
      setIsModalOpen(false);
      setSelectedRoute(null);
      setComment("");
      setAttachedFilesOther([]);
      setAttachedFilesBgDraft([]);
    }
  };

  // Открытие модалки с выбранным маршрутом
  const openModalForRoute = (route: TaskRoute) => {
    setSelectedRoute(route);
    setComment("");
    setAttachedFilesOther([]);
    setAttachedFilesBgDraft([]);
    setIsModalOpen(true);
  };

  const onSelectRoute = (routeKey: string) => {
    const route = routes.find((r) => r.key === routeKey) || { key: routeKey, value: routeKey };
    openModalForRoute(route);
  };

  // Подтверждение в модалке
  const confirmModal = async () => {
    if (!selectedRoute) return;
    await handleSubmit(selectedRoute.key, comment, attachedFilesOther, attachedFilesBgDraft);
  };

  const cancelModal = () => {
    if (submitting) return; // не даём закрыть во время отправки
    setIsModalOpen(false);
    setSelectedRoute(null);
    setComment("");
    setAttachedFilesOther([]); // очистка при отмене
    setAttachedFilesBgDraft([]);
  };

  const hasBgDraft = model?.components?.orderDocuments?.some(d => d.docType === DOC_BG_DRAFT) || false;

  return (
    <div>
      <div style={contentStyle}>
        {order && (
          <BankTaskInfo
            task={model?.components?.bankTask}
            orderId={orderId}
            order={order}
            onRefresh={async () => {
              const res = await loadData();
              await ensureReworkHasTaskOrStatusChanged(res);
            }}
          />
        )}

        <div className={styles.cardsRow}>
          <CustomerCard
            customer={model?.components?.principal}
            className={styles.cardStretch}
          />
          <ProductBgCard
            product={model?.components?.productBgCard}
            className={styles.cardStretch}
          />
        </div>

        <div className={styles.cardsRow}>
          <DocBgCard
            docs={model?.components?.orderDocuments}
            className={styles.cardStretch}
          />
          <SignDocsCard
            signPage={model?.components?.signPage}
            className={styles.cardStretch}
          />
          <BankOffer
            commission={model?.components?.commission}
            docs={model?.components?.orderDocuments} // Счёт на оплату комиссии
            className={styles.cardStretch}
          />
        </div>

        <Tabs
          defaultActiveKey="1"
          items={[
            {
              label: "Комментарии",
              key: "1",
              children: <Comments comments={model?.components?.comments || []} />,
            },
            {
              label: "Документы по заявке",
              key: "2",
              children: (
                <>
                  <DocDownloadCard title="Анкета клиента по форме банка" docType="bank_principalForm" docs={model?.components?.orderDocuments} />
                  <DocDownloadCard title="Справка об отсутствии необходимости одобрения сделки" docType="bg_woAppLot" docs={model?.components?.orderDocuments} />
                  <DocDownloadCard title="Согласие на работу с агентом" docType="bg_agentRelationAgreement" docs={model?.components?.orderDocuments} />
                  <DocDownloadCard title="Прочие документы" docType="otherOrderDocs" docs={model?.components?.orderDocuments} />
                </>
              )
            },
          ]}
        />
      </div>

      {routes.length > 0 && (
        <div style={footerStyle}>
          <TaskRouteButtons routes={routes} onSelect={onSelectRoute} disabled={submitting} />
        </div>
      )}

      <Modal
        open={isModalOpen}
        onOk={confirmModal}
        onCancel={cancelModal}
        okText={selectedRoute?.value || "Подтвердить"}
        cancelText="Отмена"
        confirmLoading={submitting}
        title={MODAL_TITLE}
        maskClosable={!submitting}
        keyboard={!submitting}
      >
        <Input.TextArea
          rows={4}
          value={comment}
          placeholder="Комментарий (необязательно)"
          onChange={(e) => setComment(e.target.value)}
        />

        {hasBgDraft ? (
          <>
            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Предложить свой макет гарантии</div>
              <FilesAttach value={attachedFilesBgDraft} onChange={setAttachedFilesBgDraft} multiple={false} />
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Прочие документы</div>
              <FilesAttach value={attachedFilesOther} onChange={setAttachedFilesOther} />
            </div>
          </>
        ) : (
          <div style={{ marginTop: 12 }}>
            <FilesAttach value={attachedFilesOther} onChange={setAttachedFilesOther} />
          </div>
        )}
      </Modal>
    </div>
  );
}
